import { IFilters, Technician } from '@agroone/entities'
import { Injectable } from '@angular/core'
import { requireDefined } from '@agroone-front/shared'
import { SelectItem } from '../../models/select-item.model'
import { BehaviorSubject, Observable } from 'rxjs'

/** The value of a site group of the forecasted stocks filters form */
export interface SiteFilter {
  siteId: number
  siteName: string
  color: string | null
  stocks: { name: string; selected: boolean }[]
}

export interface ForecastedStocksFilters {
  sites: SiteFilter[]
}

export const defaultFilters: IFilters = {
  cropType: null,
  region: null,
  regionId: null,
  technicians: null,
  varieties: null,
  specification: null,
  varietalType: null,
  sectors: null,
  type: null,
  forecastHarvestDate: null,
  floweringDate: null,
  plantedDate: null,
  onlyOpenCrops: false,
  offerStatuses: [],
}

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  public filterSubject: BehaviorSubject<IFilters> = new BehaviorSubject(defaultFilters)
  public filters$: Observable<IFilters> = this.filterSubject.asObservable()
  get filters(): IFilters {
    return this.filterSubject.getValue()
  }

  init(regionId: number | null, regionName: string | null) {
    defaultFilters.regionId = regionId
    defaultFilters.region = regionName
    // initialize observable
    if (localStorage.getItem('filterInfos')) {
      const data: IFilters = requireDefined(this.getStoredFilters(), 'the stored filters')
      if (data.regionId !== regionId) {
        data.regionId = regionId
        data.region = regionName
        this.saveToLocalStorage('filterInfos', defaultFilters)
      } else {
        this.filterSubject.next(data)
      }
    } else {
      this.saveToLocalStorage('filterInfos', defaultFilters)
    }
  }

  public setFilter(value: IFilters): void {
    this.filterSubject.next(value)

    const filterLocal: IFilters = requireDefined(this.getStoredFilters(), 'the stored filters')

    // sort sur la colonne + ACS / DESC
    const filter: IFilters = {
      cropType: value.cropType || filterLocal.cropType || null,
      region: value.region ?? filterLocal.region ?? null,
      regionId: value.regionId ?? filterLocal.regionId ?? null,
      technicians: value.technicians ?? [],
      varieties: value.varieties ?? [],
      specification: value.specification ?? null,
      varietalType: value.varietalType ?? filterLocal.varietalType ?? null,
      sectors: value.sectors ?? filterLocal.sectors ?? [],
      type: value.type ?? null,
    }

    this.saveToLocalStorage('filterInfos', filter)
  }

  /**
   * @deprecated remove after migration
   */
  public techniciansByRegion(technicians: Technician[], region: string): SelectItem[] {
    const techniciansValue: SelectItem[] = technicians
      ?.filter((technician) =>
        requireDefined(technician.availableRegions, 'the technician regions').find(
          (availableRegion) => availableRegion === region,
        ),
      )
      ?.map((technician) => ({
        label: technician.email?.split('@')?.[0],
        value: technician.email,
      }))
    return techniciansValue.sort((x, y) => x.label.localeCompare(y.label))
  }

  private saveToLocalStorage(key: string, data: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(data))
    } catch (error) {
      console.error(`Failed to save ${key} to localStorage:`, error)
    }
  }

  public getStoredFilters(): IFilters | null {
    const data = localStorage.getItem('filterInfos')
    try {
      return data ? JSON.parse(data) : null
    } catch {
      console.warn('Invalid filterInfos data in localStorage')
      return null
    }
  }

  public setForecastedStocksFilters(
    cropType: string,
    regionId: IFilters['regionId'],
    filters: ForecastedStocksFilters,
  ) {
    localStorage.setItem(`forecastedStocksFilters_${cropType}_${regionId}`, JSON.stringify(filters))
  }

  public getForecastedStocksFilters(cropType: string, regionId: IFilters['regionId']): ForecastedStocksFilters | null {
    const key = `forecastedStocksFilters_${cropType}_${regionId}`
    const data = localStorage.getItem(key)
    try {
      return data ? JSON.parse(data) : null
    } catch {
      console.warn('Invalid forecastedStocksFilters data in localStorage')
      return null
    }
  }
}

export { IFilters }
